import React, { useEffect, useRef, useState } from 'react'
import { TranslationProvider } from '~/components/shared/TranslationProvider'
import BenefitsProvider, { useBenefits } from '../BenefitsProvider'
import ScheduleDialog from '../ProfessionalServices/ScheduleDialog'
import { useTranslation } from '~/hooks/useTranslation'
import translations from '../locales.json'
import { Button } from '~/lib/ui/button'
import {
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline'
import { cn } from '~/lib/utils'
import { Step } from './Step'
import { Toaster } from '~/lib/ui/toast'

const ResumeReview = ({ ...props }) => {
  const { t } = useTranslation()
  const { current_user } = props
  const { resumeReview, onScheduleClick, coach } = useBenefits()

  const [isCollapsed, setIsCollapsed] = useState(resumeReview !== null) // Manage collapsed state

  const scheduleScrollRef = useRef(null)
  const resumeReviewScrollRef = useRef(null)
  const reviewCompleteScrollRef = useRef(null)

  const params = window.location.search

  const meetingViaCalendly = resumeReview?.meeting_via_calendly

  useEffect(() => {
    if (params.includes('utm_campaign=resume-rev-coach-assigned')) {
      scheduleScrollRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    } else if (params.includes('utm_campaign=resume-rev')) {
      resumeReviewScrollRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    } else if (params.includes('utm_campaign=res-com')) {
      reviewCompleteScrollRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [params])

  useEffect(() => {
    if (resumeReview) {
      setIsCollapsed(true)
    }
  }, [resumeReview])

  const steps = [
    'chooseReviewType',
    'submitResume',
    'scheduleMeeting',
    'receiveFeedback',
    'askForRevisions',
    'finalFeedback',
  ].filter((step) => !(step === 'scheduleMeeting' && !meetingViaCalendly))

  return (
    <>
      <div
        className={cn(
          'mx-3 -mt-6 mb-4 flex gap-6 rounded-3xl border border-cod-gray-200 bg-white p-4 font-roboto text-cod-gray-800 transition-all duration-300 md:mx-8 md:-mt-10',
          isCollapsed
            ? 'h-full md:h-28 md:px-9 md:py-2'
            : 'h-full flex-col md:p-9 ',
        )}
      >
        <ScheduleDialog
          current_user={current_user}
          coach={coach}
          flexResumeReview={true}
        />

        <div className="flex flex-grow flex-col items-center md:flex-row md:gap-20">
          <div className="flex flex-grow flex-col gap-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-intoo-green-coachcta p-3">
                <DocumentTextIcon className="h-5 w-5 text-white" />
              </div>
              <h2 className="subtitle text-intoo-green-coachcta">
                {t('overview.title')}
              </h2>
            </div>
            {!isCollapsed && (
              <>
                <p className="!mb-0">{t('overview.intro1')}</p>
                <p className="!mb-0">{t('overview.intro2')}</p>
              </>
            )}
          </div>
          <img
            alt="people network"
            src="https://www-yns-com.s3.amazonaws.com/interview/prepare_to_interview/flex_coach_image.png"
            className={isCollapsed ? 'h-20' : 'h-full'}
          />
          {resumeReview && (
            <button onClick={() => setIsCollapsed(!isCollapsed)}>
              {isCollapsed ? (
                <ChevronDownIcon className="h-10 w-10" />
              ) : (
                <ChevronUpIcon className="h-10 w-10" />
              )}
            </button>
          )}
        </div>

        {!isCollapsed && (
          <div className="grid gap-12 md:grid-cols-2">
            <div className="flex flex-col justify-between rounded-3xl border border-[#2F2F2F] bg-willow-brook p-6">
              <div>
                <h3 className="subtitle mb-6 text-intoo-green-coachcta">
                  {t('meetingWithCoach.title')}
                </h3>
                <p className="!mb-6">{t('meetingWithCoach.p1')}</p>
                <p className="!mb-6">{t('meetingWithCoach.p2')}</p>
              </div>
              <Button
                variant="coachcta"
                onClick={() =>
                  onScheduleClick(
                    'ServiceRequest::ResumeReview',
                    t('meetingWithCoach.button'),
                    true,
                  )
                }
                className="w-full"
                disabled={resumeReview}
              >
                {t('meetingWithCoach.button')}
              </Button>
            </div>

            <div className="flex h-full flex-col justify-between rounded-3xl border border-[#2F2F2F] bg-willow-brook p-6">
              <div>
                <h3 className="subtitle mb-6 text-intoo-green-coachcta">
                  {t('platformReview.title')}
                </h3>
                <p className="!mb-6">{t('platformReview.p1')}</p>
              </div>
              <Button
                variant="coachcta"
                onClick={() =>
                  onScheduleClick(
                    'ServiceRequest::ResumeReview',
                    t('platformReview.button'),
                    false,
                  )
                }
                className="w-full"
                disabled={resumeReview}
              >
                {t('platformReview.button')}
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="mx-3 mb-4 pb-4 pt-4 font-roboto text-cod-gray-800 md:mx-8 md:mb-9 md:w-3/4">
        <div>
          {steps.map((stepKey, idx) => (
            <Step
              key={`step-${stepKey}-${idx}`}
              stepKey={stepKey}
              idx={idx}
              steps={steps}
              scheduleScrollRef={scheduleScrollRef}
              resumeReviewScrollRef={resumeReviewScrollRef}
              reviewCompleteScrollRef={reviewCompleteScrollRef}
            />
          ))}
        </div>
      </div>
      <Toaster />
    </>
  )
}

const Index = ({ ...props }) => {
  const { locale } = props
  return (
    <TranslationProvider
      translations={translations}
      locale={locale}
      scope="javascript.components.coach_services.ResumeReview"
    >
      <BenefitsProvider {...props}>
        <ResumeReview {...props} />
      </BenefitsProvider>
    </TranslationProvider>
  )
}

export default Index
